import { useCallback, useMemo, useState } from 'react';
import type { SaveData, SaveResult } from '../../../types/save';
import type { ChecklistGroup } from '../types/checklist';
import { useValidation } from '../../../hooks/useValidation';
import { sendToTerminal, terminalLog } from '../../../lib/terminalLogger';
import { useFilePathBuilder } from '../../../hooks/useFilePathBuilder';
import { usePathResolver } from '../../../hooks/usePathResolver';
import { storage } from '../../../lib/storage/storage';
import { Keys } from '../../../lib/storage/keys';
import { lockManager } from '../../../lib/corel/lockManager';
import { corelCommunication } from '../../../lib/corel/communication';
import { OPERATION_DELAYS, pipelineSleep } from '../../../lib/corel/pipeline/pipelineConstants';
import { requestConfirmation } from '../../../lib/globalConfirm';
import { monitoradorObjetos } from '../../../lib/corel/MonitoradorObjetos';
import { MetadataService } from '../../../lib/corel/actions/metadata';

export type UseSaveDataParams = {
  saveData?: SaveData;
  checklists: ChecklistGroup[];
  totals: { allChecked: boolean; progress: number };
  role: string;
  onSave: () => void;
  mode?: 'default' | 'temp';
  tempPathBuilder?: (saveData: SaveData) => Promise<{ filePath: string | null; fileName: string | null }>;
};

export type UseSaveDataReturn = {
  canSave: boolean;
  issues: { code: string; message: string; severity?: 'info' | 'warning' | 'error' }[];
  showModal: boolean;
  openSave: () => Promise<SaveResult>;
  onModalComplete: () => void;
};

export function useSaveData({ saveData, checklists, totals, role, onSave, mode = 'default', tempPathBuilder }: UseSaveDataParams): UseSaveDataReturn {
  const { validateBeforeSave } = useValidation();
  const { buildFilePath } = useFilePathBuilder();
  const { resolvePath } = usePathResolver();
  const [showModal, setShowModal] = useState(false);

  const { issues } = useMemo(() => validateBeforeSave(saveData, { allChecked: totals.allChecked }), [saveData, totals.allChecked, validateBeforeSave]);

  const canSave = useMemo(() => {
    const hasOrcId = Boolean(saveData?.pedido?.orc_id);
    const isNumeric = hasOrcId ? /^\d+$/.test(String(saveData!.pedido!.orc_id)) : false;
    return Boolean(totals.allChecked && hasOrcId && isNumeric);
  }, [saveData, totals.allChecked]);

  const openSave = useCallback<() => Promise<SaveResult>>(async () => {
    // ✅ NOTA: Metadados serão coletados DEPOIS do unlock (FASE_4.3)
    // Não validar aqui - serão coletados quando o documento estiver livre

    // Bloqueios essenciais
    if (!saveData?.pedido?.orc_id) {
      return { ok: false, error: 'Número de pedido (orc_id) ausente.' };
    }
    if (!/^\d+$/.test(String(saveData.pedido.orc_id))) {
      return { ok: false, error: 'Formato inválido para orc_id. Deve conter apenas números.' };
    }
    if (!totals.allChecked) {
      return { ok: false, error: 'Checklist não está completamente marcado.' };
    }

    // Alerta apenas no modo default (produção). No TEMP não usamos regras da API.
    if (mode !== 'temp') {
      if (!saveData?.regras?.diretorio_nome) {
        const alertMessage = `ATENÇÃO: Diretório específico não encontrado nas regras do produto!\n\nO arquivo será salvo em um diretório genérico "INDEFINIDO", o que pode dificultar sua localização posteriormente.\n\nPor favor, informe ao administrador do sistema para verificar as regras do produto "${saveData?.produto?.nome || 'atual'}" na API.\n\nDeseja continuar mesmo assim?`;
        const confirmed = await requestConfirmation({
          title: 'Diretório não encontrado',
          message: alertMessage,
          confirmLabel: 'Continuar',
          cancelLabel: 'Cancelar'
        });
        if (!confirmed) {
          return { ok: false, error: 'Operação cancelada pelo usuário.' };
        }
      }
    }

    // Determinar o caminho: TEMP usa builder local; default usa backend PathResolver
    let filePath: string | null = null;
    let fileNameForLog: string | null = null;
    let storageMethod: 'smb' | 'gdrive' | undefined;
    if (mode === 'temp' && tempPathBuilder) {
      try {
        sendToTerminal('📄 [CHECKLIST:TEMP] chamando tempPathBuilder (async)');
        const built = await tempPathBuilder(saveData);
        filePath = built.filePath;
        fileNameForLog = built.fileName;
        sendToTerminal('📄 [CHECKLIST:TEMP] caminho calculado', { filePath, fileName: fileNameForLog });
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        sendToTerminal('❌ [CHECKLIST:TEMP] erro ao calcular caminho', { error: errorMsg });
        return { ok: false, error: `Erro ao calcular caminho: ${errorMsg}` };
      }
    } else {
      // Tentar resolver via backend (PathResolverService) com retry automático
      try {
        const resolved = await resolvePath(saveData, role, { useCVIfVariavel: false });
        filePath = resolved.filePath;
        fileNameForLog = resolved.fileName || null;
        storageMethod = resolved.storageMethod;
        sendToTerminal('✅ [CHECKLIST] Path resolvido via backend', { filePath, storageMethod });
      } catch (backendError) {
        // Backend falhou após todas as tentativas de retry — perguntar ao usuário
        const errorMsg = backendError instanceof Error ? backendError.message : String(backendError);
        sendToTerminal('⚠️ [CHECKLIST] Backend indisponível após retries', { error: errorMsg });

        const useFallback = await requestConfirmation({
          title: 'Backend indisponível',
          message: `Não foi possível resolver o caminho do arquivo via servidor.\n\nDeseja usar o cálculo local como alternativa?\n\n(Erro: ${errorMsg})`,
          confirmLabel: 'Usar cálculo local',
          cancelLabel: 'Cancelar salvamento',
        });

        if (!useFallback) {
          return { ok: false, error: 'Salvamento cancelado — backend indisponível.' };
        }

        sendToTerminal('🔄 [CHECKLIST] Usuário aceitou fallback local');
        const result = buildFilePath(saveData, role, { useCVIfVariavel: false });
        filePath = result.filePath;
        fileNameForLog = result.fileName || null;
      }
    }

    // Persistir caminho usando storage unificado com dual-write
    const dadosComCaminho = {
      ...saveData,
      caminho_arquivo: filePath,
      ...(storageMethod ? { storageMethod } : {}),
      ...(mode === 'temp' ? { fluxo: { tipo: 'temp' as const } } : {})
    } as SaveData & { caminho_arquivo?: string | null; fluxo?: { tipo: 'temp' } };
    
    // ✅ SEMPRE usar 'session' para consistência - o SaveProgressModal lê de 'session'
    const storageArea: 'session' | 'local' = 'session';
    
    try {
      storage.dualWrite(
        Keys.export.currentSaveData,
        ['currentSaveData'], // Chave legada
        dadosComCaminho,
        { area: storageArea, enableDualWrite: true }
      );
      
      sendToTerminal('💾 [CHECKLIST] Template persistido', { 
        target: 'Keys.export.currentSaveData (dual-write)', 
        hasCaminhoArquivo: Boolean(filePath),
        area: storageArea // Log da área usada para debug
      });
      if (mode === 'temp') {
        try {
          sendToTerminal('💾 [CHECKLIST:TEMP] Checklist armazenado', {
            area: storageArea,
            hasFluxo: !!(dadosComCaminho as any)?.fluxo,
            hasPath: Boolean(filePath),
            fileName: fileNameForLog || null
          });
        } catch {}
      }
    } catch (e) {
      sendToTerminal('❌ [CHECKLIST] Erro ao persistir SaveData', {
        error: e instanceof Error ? e.message : String(e),
        stack: e instanceof Error ? e.stack : undefined
      });
      terminalLog.warn('[CHECKLIST] Falha ao persistir SaveData', { error: e });
    }

    // Checklist final para eventual auditoria/log
    try {
      const checklistCompletado = checklists.map(group => ({
        titulo: group.title,
        itensVerificados: group.items.filter(item => item.checked).map(item => item.text),
      }));
      terminalLog.debug('[CHECKLIST] Checklist completado', { checklistCompletado, fileNameForLog });
    } catch {}

    // Helper: Parada COMPLETA do monitor para salvamento
    // Para o C# + limpa handlers + marca flag stoppedForSave
    const stopMonitorCompletelyForSave = async (): Promise<{ ok: boolean; error?: string }> => {
      const MAX_RETRIES = 3;

      sendToTerminal(`🛑 [CHECKLIST] Parando monitor COMPLETAMENTE para salvamento`);

      // Usar novo método que para 100% (C# + handlers + flag)
      const stopResult = await monitoradorObjetos.stopCompletelyForSave(MAX_RETRIES, 2000);

      if (stopResult.success) {
        sendToTerminal('✅ [CHECKLIST] Monitor parado COMPLETAMENTE', {
          stoppedForSave: monitoradorObjetos.isStoppedForSave()
        });
        return { ok: true };
      }

      // Mesmo se falhou no C#, os handlers já foram limpos
      sendToTerminal('⚠️ [CHECKLIST] Falha ao parar no C#, mas handlers foram limpos', {
        error: stopResult.error,
        stoppedForSave: monitoradorObjetos.isStoppedForSave()
      });

      // Considerar sucesso parcial - o importante é que handlers foram limpos
      return { ok: true };
    };

    // 🆕 Parar monitor e desativar lock antes de salvar
    try {
      sendToTerminal('━━━━━━ [FASE_4] INÍCIO: Pré-Salvamento ━━━━━━');
      
      // 🛑 PASSO 1: Verificar e parar monitoramento antes de salvar
      // ✅ O monitor permanece ATIVO durante o checklist para permitir visualização em tempo real
      // Esta verificação é CRÍTICA para garantir que o monitor seja parado antes do salvamento
      sendToTerminal('🔍 [FASE_4.1] Verificando status do monitor...');
      
      const statusResult = await monitoradorObjetos.verificarStatus();
      const monitorAtivo = statusResult?.ativo === true;
      
      sendToTerminal('📊 [FASE_4.1] Status do monitor', { 
        ativo: monitorAtivo,
        docKey: statusResult?.docKey 
      });
      
      // 🛑 SEMPRE parar monitor COMPLETAMENTE antes de salvar
      // Não importa se está ativo ou não - queremos garantir que:
      // 1. Handlers sejam limpos
      // 2. Flag stoppedForSave seja marcado
      // 3. Nenhum evento de documento seja processado durante salvamento
      sendToTerminal('🛑 [FASE_4.1] Parando monitor COMPLETAMENTE para salvamento...');

      const stopResult = await stopMonitorCompletelyForSave();

      if (!stopResult.ok) {
        sendToTerminal('❌ [FASE_4.1] Falha crítica ao parar monitor');
        return { ok: false, error: 'Não foi possível parar o monitor para salvamento' };
      }

      sendToTerminal('✅ [FASE_4.1] Monitor parado COMPLETAMENTE', {
        monitorAtivo,
        stoppedForSave: monitoradorObjetos.isStoppedForSave()
      });

      // Aguardar estabilização
      sendToTerminal('⏱️ [FASE_4.1] Sleep: 200ms (AFTER_MONITOR_STOP)');
      await pipelineSleep(OPERATION_DELAYS.AFTER_MONITOR_STOP, 'after_monitor_stop');
      
      // 🔓 PASSO 2: Desativar lock-document e AGUARDAR confirmação do C#
      sendToTerminal('🔓 [FASE_4.2] Desativando lock-document antes de salvar');
      const unlockResult = await lockManager.unlock(3);
      
      // Validar resultado do unlock
      if (!unlockResult.ok) {
        if (unlockResult.code === 'lock_not_active') {
          // Lock já estava inativo - OK
          sendToTerminal('✅ [FASE_4.2] Lock-document já estava inativo');
        } else {
          // Erro ao desativar lock - CRÍTICO
          sendToTerminal('❌ [FASE_4.2] Falha ao desativar lock-document após múltiplas tentativas', {
            attempts: unlockResult.attempts,
            error: unlockResult.error
          });
          return { 
            ok: false, 
            error: `Não foi possível desativar o lock-document após ${unlockResult.attempts} tentativas. Documento ainda está em modo seguro.` 
          };
        }
      } else {
        // ✅ Validar que o documento realmente saiu do modo seguro
        if (unlockResult.locked === true) {
          sendToTerminal('❌ [FASE_4.2] Inconsistência detectada: unlock retornou ok mas locked ainda é true', {
            attempts: unlockResult.attempts
          });
          return { 
            ok: false, 
            error: 'Documento ainda está em modo seguro após desativar lock. Não é possível continuar.' 
          };
        }
        
        sendToTerminal('✅ [FASE_4.2] Lock-document desativado com sucesso', {
          attempts: unlockResult.attempts,
          locked: unlockResult.locked
        });
      }
      
      // Aguardar documento sair completamente do modo seguro
      sendToTerminal('⏱️ [FASE_4.2] Sleep: 500ms (documento sair do modo seguro)');
      await pipelineSleep(500, 'after_unlock_document');
      
      // ✅ NOVO: Coletar metadados DEPOIS do unlock (documento está livre)
      sendToTerminal('📤 [FASE_4.3] Coletando metadados iniciais (documento livre)');
      try {
        const metadataResult = await MetadataService.collectFull(true);
        
        if (!metadataResult.sucesso) {
          sendToTerminal('❌ [FASE_4.3] Falha ao coletar metadados após unlock', {
            totalObjetos: metadataResult.totalObjetos || 0,
            totalPaginas: metadataResult.totalPaginas || 0
          });
          return { 
            ok: false, 
            error: 'Falha ao coletar metadados do documento. Tente novamente.' 
          };
        }
        
        // Validar que objetos foram coletados
        if (metadataResult.totalObjetos === 0) {
          sendToTerminal('⚠️ [FASE_4.3] Metadados coletados mas nenhum objeto encontrado', {
            totalPaginas: metadataResult.totalPaginas || 0
          });
          // Continuar mesmo assim - pode ser documento vazio intencionalmente
        }
        
        // Salvar metadados no sessionStorage
        const wrappedPayload = { status: 'ok', result: metadataResult };
        storage.set(Keys.export.initialMetadataSnapshot, wrappedPayload, { 
          area: 'session', 
          ttlMs: 10 * 60 * 1000 
        });
        
        sendToTerminal('✅ [FASE_4.3] Metadados coletados com sucesso após unlock', {
          totalObjetos: metadataResult.totalObjetos,
          totalPaginas: metadataResult.totalPaginas,
          hasSnapshot: !!metadataResult.snapshot
        });
      } catch (metadataError) {
        const errorMessage = metadataError instanceof Error ? metadataError.message : 'Erro desconhecido';
        sendToTerminal('❌ [FASE_4.3] Erro ao coletar metadados após unlock', { error: errorMessage });
        return { 
          ok: false, 
          error: `Erro ao coletar metadados: ${errorMessage}` 
        };
      }
      
      // Agora sim, abrir modal de salvamento (delegar fluxo ao SaveProgressModal)
      sendToTerminal('━━━━━━ [FASE_4] FIM → Abrindo SaveProgressModal ━━━━━━');
      sendToTerminal('🎬 [FASE_4.4] setShowModal(true)');
      setShowModal(true);
      
    } catch (e) {
      sendToTerminal('❌ [FASE_4] Erro ao preparar salvamento', { error: e instanceof Error ? e.message : String(e) });
      return { ok: false, error: e instanceof Error ? e.message : 'Erro ao preparar salvamento' };
    }

    return { ok: true, filePath };
  }, [buildFilePath, resolvePath, checklists, role, saveData, totals.allChecked, mode, tempPathBuilder]);

  const onModalComplete = useCallback(() => {
    // Finalização e limpeza - silencioso
    
    try {
      if (saveData) {
        if (mode === 'temp' && tempPathBuilder) {
          const built = tempPathBuilder(saveData);
          if (built?.fileName) terminalLog.debug('[CHECKLIST] Nome do arquivo gerado (TEMP)', { fileName: built.fileName });
        } else {
          const { fileName } = buildFilePath(saveData, role, { useCVIfVariavel: true });
          terminalLog.debug('[CHECKLIST] Nome do arquivo gerado', { fileName });
        }

        type RuntimeSaveData = {
          configuracoes?: { contracoesVariaveis?: boolean };
          objetos?: Array<{ contraction?: string; nome: string }>;
        };
        const runtime = saveData as unknown as RuntimeSaveData;
        if (runtime.configuracoes?.contracoesVariaveis) {
          const objectsByContraction: Record<string, string[]> = {};
          if (Array.isArray(runtime.objetos)) {
            runtime.objetos.forEach(obj => {
              const contraction = obj.contraction || '0px';
              if (!objectsByContraction[contraction]) objectsByContraction[contraction] = [];
              objectsByContraction[contraction].push(obj.nome);
            });
          }
          terminalLog.debug('[CHECKLIST] Pedido com contrações variáveis (CV)', { objectsByContraction });
        }
      }
    } catch (error) {
      sendToTerminal('❌ [CHECKLIST] Erro ao gerar nome de arquivo', { error: error instanceof Error ? error.message : String(error) });
    }

    // Limpeza do estado de bloqueio após 15 segundos
    setTimeout(() => {
      try {
        localStorage.removeItem('lastSaveRequest');
        localStorage.removeItem('lastSaveRequestId');
        terminalLog.debug('[CHECKLIST] Estado de bloqueio de salvamento limpo');
      } catch (error) {
        sendToTerminal('❌ [CHECKLIST] Erro ao limpar estado de bloqueio', { error: error instanceof Error ? error.message : String(error) });
      }
    }, 15000);

    storage.remove(Keys.export.initialMetadataSnapshot, 'session');
    
    sendToTerminal('🔄 [FASE_8] Fechando modal de salvamento...');
    setShowModal(false);
    
    sendToTerminal('✅ [FASE_8] Chamando onSave() do parent');
    onSave();

    sendToTerminal('━━━━━━ [FASE_8] FIM: Finalização Concluída ━━━━━━');
  }, [buildFilePath, onSave, role, saveData, mode, tempPathBuilder]);

  return { canSave, issues, showModal, openSave, onModalComplete };
}


